import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../../../contexts/ToastContext';
import { apiService } from '../../../../services/api';
import styles from './NotificationPreferences.module.css';

interface PreferenceRow {
    notificationType: number;
    typeName: string;
    emailEnabled: boolean;
    inAppEnabled: boolean;
    pushEnabled: boolean;
}

const NOTIFICATION_TYPE_LABELS: Record<string, string> = {
    NewAppointment: 'New Appointments',
    AppointmentCancelled: 'Appointment Cancellations',
    AppointmentCompleted: 'Appointment Completions',
    NewStaffJoined: 'New Staff Joined',
    PromotionExpiring: 'Expiring Promotions',
    System: 'System Notifications',
    NewBusinessCreated: 'New Business Created',
    NewUserRegistered: 'New User Registered',
    OnboardingApproved: 'Onboarding Approved',
    OnboardingRejected: 'Onboarding Rejected',
    OnboardingSubmitted: 'Onboarding Submitted',
};

const NotificationPreferences: React.FC = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [preferences, setPreferences] = useState<PreferenceRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [pushSupported, setPushSupported] = useState(false);
    const [pushSubscribed, setPushSubscribed] = useState(false);

    const fetchPreferences = useCallback(async () => {
        try {
            setLoading(true);
            const response = await apiService.getNotificationPreferences();
            setPreferences(response.preferences || []);
        } catch {
            showToast('Failed to load notification preferences', 'error');
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    useEffect(() => {
        fetchPreferences();
        checkPushSupport();
    }, [fetchPreferences]);

    const checkPushSupport = async () => {
        if ('serviceWorker' in navigator && 'PushManager' in window) {
            setPushSupported(true);
            try {
                const registration = await navigator.serviceWorker.getRegistration('/sw-push.js');
                if (registration) {
                    const subscription = await registration.pushManager.getSubscription();
                    setPushSubscribed(!!subscription);
                }
            } catch {
                // Push check failed silently
            }
        }
    };

    const handleToggle = (index: number, channel: 'emailEnabled' | 'inAppEnabled' | 'pushEnabled') => {
        setPreferences(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [channel]: !updated[index][channel] };
            return updated;
        });
        setDirty(true);
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            const payload = preferences.map(p => ({
                notificationType: p.notificationType,
                emailEnabled: p.emailEnabled,
                inAppEnabled: p.inAppEnabled,
                pushEnabled: p.pushEnabled,
            }));
            await apiService.updateNotificationPreferences(payload);
            showToast('Notification preferences saved', 'success');
            setDirty(false);
        } catch {
            showToast('Failed to save preferences', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleEnablePush = async () => {
        try {
            // Register service worker
            const registration = await navigator.serviceWorker.register('/sw-push.js');
            await navigator.serviceWorker.ready;

            // Request permission
            const permission = await Notification.requestPermission();
            if (permission !== 'granted') {
                showToast('Push notification permission denied', 'error');
                return;
            }

            // Subscribe to push
            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: process.env.REACT_APP_VAPID_PUBLIC_KEY
            });

            const p256dh = subscription.getKey('p256dh');
            const auth = subscription.getKey('auth');

            if (p256dh && auth) {
                await apiService.subscribeWebPush({
                    endpoint: subscription.endpoint,
                    p256dhKey: btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(p256dh)))),
                    authKey: btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(auth))))
                });
                setPushSubscribed(true);
                showToast('Push notifications enabled', 'success');
            }
        } catch (err) {
            showToast('Failed to enable push notifications', 'error');
        }
    };

    const handleDisablePush = async () => {
        try {
            const registration = await navigator.serviceWorker.getRegistration('/sw-push.js');
            if (registration) {
                const subscription = await registration.pushManager.getSubscription();
                if (subscription) {
                    await apiService.unsubscribeWebPush(subscription.endpoint);
                    await subscription.unsubscribe();
                }
            }
            setPushSubscribed(false);
            showToast('Push notifications disabled', 'success');
        } catch {
            showToast('Failed to disable push notifications', 'error');
        }
    };

    if (loading) {
        return <div className={styles.loading}>Loading preferences...</div>;
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <button className={styles.backButton} onClick={() => navigate('/settings')}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>
                </button>
                <h2 className="xH1">Notification Preferences</h2>
            </div>

            {pushSupported && (
                <div className={styles.pushSection}>
                    <h3>Browser Push Notifications</h3>
                    <div className={styles.pushRow}>
                        <div className={styles.pushInfo}>
                            <h4>Push Notifications</h4>
                            <p>Receive notifications even when the browser tab is closed</p>
                        </div>
                        {pushSubscribed ? (
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                <span className={`${styles.pushStatus} ${styles.pushEnabled}`}>Enabled</span>
                                <button className={styles.pushButton} onClick={handleDisablePush} style={{ background: 'var(--destructive-fill)' }}>
                                    Disable
                                </button>
                            </div>
                        ) : (
                            <button className={styles.pushButton} onClick={handleEnablePush}>
                                Enable Push
                            </button>
                        )}
                    </div>
                </div>
            )}

            <div className={styles.card}>
                <div className={styles.tableHeader}>
                    <span>Notification Type</span>
                    <span>Email</span>
                    <span>In-App</span>
                    <span>Push</span>
                </div>
                {preferences.map((pref, index) => (
                    <div key={pref.notificationType} className={styles.prefRow}>
                        <span className={styles.prefLabel}>
                            {NOTIFICATION_TYPE_LABELS[pref.typeName] || pref.typeName}
                        </span>
                        <div className={styles.toggleCell}>
                            <input
                                type="checkbox"
                                className={styles.toggle}
                                checked={pref.emailEnabled}
                                onChange={() => handleToggle(index, 'emailEnabled')}
                            />
                        </div>
                        <div className={styles.toggleCell}>
                            <input
                                type="checkbox"
                                className={styles.toggle}
                                checked={pref.inAppEnabled}
                                onChange={() => handleToggle(index, 'inAppEnabled')}
                            />
                        </div>
                        <div className={styles.toggleCell}>
                            <input
                                type="checkbox"
                                className={styles.toggle}
                                checked={pref.pushEnabled}
                                onChange={() => handleToggle(index, 'pushEnabled')}
                            />
                        </div>
                    </div>
                ))}
            </div>

            <div className={styles.saveBar}>
                <button
                    className={styles.saveButton}
                    onClick={handleSave}
                    disabled={!dirty || saving}
                >
                    {saving ? 'Saving...' : 'Save Preferences'}
                </button>
            </div>
        </div>
    );
};

export default NotificationPreferences;
